const DESIGN_CHANGE_EVENT = "fut-card:design-change";
const PERSONALIZATION_STEP_EVENT = "fut-card:personalization-step";
const POPUP_SELECTOR = "[data-fut-card-personalization-popup]";
const IMAGE_OPTION_SELECTOR = "[data-fut-card-image-option]";
const PREVIEW_TEXT_COLOR_VAR = "--fut-card-preview-text-color";
const SHOPIFY_FLAG_BASE = "https://cdn.shopify.com/static/images/flags";
const NATIONALITY_COMBOBOX_SELECTOR = ".fut-card-nationality theme-combobox";
const PREVIEW_UPLOAD_PLAYER_IMAGE = "player-image";
const PREVIEW_UPLOAD_TEAM_CREST = "team-crest";
const PHOTO_MODE_UPLOAD = "upload";
const PHOTO_MODE_EXAMPLE = "example";

/** Max text width as a fraction of the name slot (lower = shrink sooner). */
const NAME_FIT_MAX_WIDTH_RATIO = 0.8;
/** Smallest allowed name size as a fraction of the default card name size. */
const NAME_MIN_FONT_SIZE_RATIO = 0.35;
/** Absolute floor for fitted name font size in px. */
const NAME_MIN_FONT_SIZE_PX = 6;

const PREVIEW_FIELD_SELECTOR = [
  "input[data-fut-card-line-item-text]",
  "input[data-fut-card-player-position]",
  "[data-fut-card-position-preset]",
  "input[data-fut-card-stat-value]",
  "input[data-fut-card-stat-label]",
  "input[data-fut-card-overall-input]",
  "input[data-fut-card-overall-override-toggle]",
  "input[data-combobox-value]",
  ".product-file-upload__input",
].join(", ");

/**
 * @typedef {{ imageUrl?: string, textColor?: string }} FutCardPreviewDesign
 */

class FutCardPreview extends HTMLElement {
  /** @type {HTMLImageElement | null} */
  #background = null;

  /** @type {HTMLElement | null} */
  #overlay = null;

  /** @type {HTMLElement | null} */
  #overall = null;

  /** @type {HTMLElement | null} */
  #position = null;

  /** @type {HTMLElement | null} */
  #name = null;

  /** @type {HTMLImageElement | null} */
  #flag = null;

  /** @type {HTMLImageElement | null} */
  #crest = null;

  /** @type {HTMLElement | null} */
  #crestPlaceholder = null;

  /** @type {HTMLImageElement | null} */
  #playerPhoto = null;

  /** @type {HTMLElement | null} */
  #playerPhotoPlaceholder = null;

  /** @type {HTMLImageElement | null} */
  #examplePlayerPhoto = null;

  /** @type {HTMLElement | null} */
  #examplePhotoLayer = null;

  /** @type {HTMLElement | null} */
  #hero = null;

  /** @type {string} */
  #examplePlayerImageUrl = "";

  /** @type {string} */
  #uploadUrl = "";

  /** @type {boolean} */
  #uploadFileSelected = false;

  /** @type {boolean} */
  #playerPhotoLoadFailed = false;

  /** @type {boolean} */
  #crestFileSelected = false;

  /** @type {boolean} */
  #crestLoadFailed = false;

  /** @type {boolean} */
  #crestTooltipOpen = false;

  /** @type {"upload" | "example"} */
  #photoMode = PHOTO_MODE_EXAMPLE;

  /** @type {Map<number, { label: HTMLElement; value: HTMLElement }>} */
  #statSlots = new Map();

  /** @type {ResizeObserver | undefined} */
  #nameFitObserver;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    this.#background = this.querySelector("[data-fut-card-preview-background]");
    this.#overlay = this.querySelector("[data-fut-card-preview-overlay]");
    this.#overall = this.querySelector("[data-fut-card-preview-overall]");
    this.#position = this.querySelector("[data-fut-card-preview-position]");
    this.#name = this.querySelector("[data-fut-card-preview-name]");
    this.#flag = this.querySelector("[data-fut-card-preview-flag]");
    this.#crest = this.querySelector("[data-fut-card-preview-crest]");
    this.#playerPhoto = this.querySelector(
      "[data-fut-card-preview-player-photo]",
    );
    this.#playerPhotoPlaceholder = this.querySelector(
      "[data-fut-card-preview-player-photo-placeholder]",
    );
    this.#examplePlayerPhoto = this.querySelector(
      "[data-fut-card-preview-example-photo]",
    );
    this.#examplePhotoLayer = this.querySelector(
      "[data-fut-card-preview-example-photo-layer]",
    );
    this.#hero = this.querySelector("[data-fut-card-preview-hero]");
    this.#crestPlaceholder = this.querySelector(
      "[data-fut-card-preview-crest-placeholder]",
    );
    this.#examplePlayerImageUrl = this.dataset.examplePlayerImage?.trim() ?? "";

    this.#statSlots = new Map();
    this.querySelectorAll("[data-fut-card-preview-stat-label]").forEach(
      (element) => {
        if (!(element instanceof HTMLElement)) return;

        const index = Number.parseInt(element.dataset.statIndex ?? "", 10);
        if (Number.isNaN(index)) return;

        const value = this.querySelector(
          `[data-fut-card-preview-stat-value][data-stat-index="${index}"]`,
        );

        if (value instanceof HTMLElement) {
          this.#statSlots.set(index, { label: element, value });
        }
      },
    );

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#playerPhoto?.addEventListener(
      "load",
      () => {
        this.#playerPhotoLoadFailed = false;
      },
      { signal },
    );
    this.#playerPhoto?.addEventListener(
      "error",
      () => {
        if (!this.#uploadUrl) return;
        this.#playerPhotoLoadFailed = true;
        this.#applyCurrentPlayerPhoto();
      },
      { signal },
    );
    this.#crest?.addEventListener(
      "load",
      () => {
        this.#crestLoadFailed = false;
      },
      { signal },
    );
    this.#crest?.addEventListener(
      "error",
      () => {
        const crestUrl = this.#crest?.getAttribute("src")?.trim() ?? "";
        if (!crestUrl) return;
        this.#crestLoadFailed = true;
        this.#applyTeamCrestDisplay("");
      },
      { signal },
    );

    const popup = this.closest(POPUP_SELECTOR);
    popup?.addEventListener(DESIGN_CHANGE_EVENT, this.#handleDesignChange, {
      signal,
    });
    popup?.addEventListener(
      PERSONALIZATION_STEP_EVENT,
      this.#scheduleTextSync,
      {
        signal,
      },
    );
    popup?.addEventListener("input", this.#handleFormInput, {
      signal,
      capture: true,
    });
    popup?.addEventListener("change", this.#handleFormInput, {
      signal,
      capture: true,
    });
    popup?.addEventListener(
      "product-file-upload:preview-change",
      this.#scheduleTextSync,
      {
        signal,
      },
    );

    this.querySelectorAll("[data-fut-card-preview-photo-mode]").forEach(
      (button) => {
        if (!(button instanceof HTMLButtonElement)) return;

        button.addEventListener("click", this.#handlePhotoModeToggle, {
          signal,
        });
      },
    );

    this.#bindCrestPlaceholderTooltip(signal);

    const nameSlot = this.#name?.closest(".fut-card-preview__name-slot");
    if (nameSlot instanceof HTMLElement) {
      this.#nameFitObserver = new ResizeObserver(() => {
        this.#fitPlayerName();
      });
      this.#nameFitObserver.observe(nameSlot);
    }

    this.#syncFromSelection();
    this.#syncTextContent();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#nameFitObserver?.disconnect();
  }

  /** @param {Event} event */
  #handlePhotoModeToggle = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const mode = button.dataset.futCardPreviewPhotoMode;
    if (mode !== PHOTO_MODE_UPLOAD && mode !== PHOTO_MODE_EXAMPLE) return;

    this.#photoMode = mode;
    this.#updatePhotoModeControls();
    this.#applyCurrentPlayerPhoto();
  };

  /** @param {CustomEvent<FutCardPreviewDesign & { value?: string }>} event */
  #handleDesignChange = (event) => {
    this.#setDesign({
      imageUrl: event.detail?.imageUrl,
      textColor: event.detail?.textColor,
    });
  };

  /** @param {Event} event */
  #handleFormInput = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!target.matches(PREVIEW_FIELD_SELECTOR)) return;

    this.#scheduleTextSync();
  };

  #scheduleTextSync = () => {
    queueMicrotask(() => {
      this.#syncTextContent();
    });
  };

  #syncFromSelection() {
    const popup = this.closest(POPUP_SELECTOR);
    if (!popup) return;

    const selectedOption =
      popup.querySelector(`${IMAGE_OPTION_SELECTOR}:checked`) ??
      popup.querySelector(IMAGE_OPTION_SELECTOR);

    if (!(selectedOption instanceof HTMLInputElement)) {
      this.#setDesign({});
      return;
    }

    this.#setDesign({
      imageUrl: selectedOption.dataset.previewImageUrl,
      textColor: selectedOption.dataset.previewTextColor,
    });
  }

  /** @param {FutCardPreviewDesign} design */
  #setDesign(design) {
    const background = this.#background;
    const overlay = this.#overlay;
    if (!background || !overlay) return;

    const imageUrl = design.imageUrl?.trim() ?? "";
    const textColor = design.textColor?.trim() ?? "";

    if (textColor) {
      this.style.setProperty(PREVIEW_TEXT_COLOR_VAR, textColor);
    } else {
      this.style.removeProperty(PREVIEW_TEXT_COLOR_VAR);
    }

    const hasDesign = imageUrl !== "";

    this.toggleAttribute("data-has-design", hasDesign);
    background.hidden = !hasDesign;
    overlay.hidden = !hasDesign;

    if (!hasDesign) {
      background.removeAttribute("src");
      return;
    }

    if (background.currentSrc !== imageUrl && background.src !== imageUrl) {
      background.src = imageUrl;
    }
  }

  #syncTextContent() {
    const popup = this.closest(POPUP_SELECTOR);
    if (!popup || !this.#overall || !this.#position || !this.#name) return;

    const nameInput = popup.querySelector(
      "input[data-fut-card-line-item-text]",
    );
    const positionInput = popup.querySelector(
      "input[data-fut-card-player-position]",
    );

    this.#name.textContent =
      nameInput instanceof HTMLInputElement ? nameInput.value.trim() : "";

    this.#fitPlayerName();

    this.#position.textContent =
      positionInput instanceof HTMLInputElement
        ? positionInput.value.trim().toUpperCase()
        : "";

    this.#overall.textContent = this.#getOverallValue(popup);

    this.#syncStats(popup);
    this.#syncNationalityFlag(popup);
    this.#syncTeamCrest(popup);
    this.#syncPlayerPhoto(popup);
  }

  #fitPlayerName() {
    const name = this.#name;
    if (!name) return;

    name.style.fontSize = "";

    const text = name.textContent?.trim() ?? "";
    if (!text) return;

    const nameSlot = name.closest(".fut-card-preview__name-slot");
    const slotWidth =
      nameSlot instanceof HTMLElement ? nameSlot.clientWidth : name.clientWidth;
    if (slotWidth <= 0) return;

    const maxTextWidth = slotWidth * NAME_FIT_MAX_WIDTH_RATIO;

    const defaultFontSize = Number.parseFloat(getComputedStyle(name).fontSize);
    if (Number.isNaN(defaultFontSize) || defaultFontSize <= 0) return;

    // inline-block + max-width: scrollWidth is the rendered text width
    if (name.scrollWidth <= maxTextWidth) return;

    const minFontSize = Math.max(
      NAME_MIN_FONT_SIZE_PX,
      defaultFontSize * NAME_MIN_FONT_SIZE_RATIO,
    );
    let low = minFontSize;
    let high = defaultFontSize;
    let bestFit = minFontSize;

    while (high - low > 0.25) {
      const mid = (low + high) / 2;
      name.style.fontSize = `${mid}px`;

      if (name.scrollWidth <= maxTextWidth) {
        bestFit = mid;
        low = mid;
      } else {
        high = mid;
      }
    }

    name.style.fontSize = `${bestFit}px`;
  }

  /**
   * @param {ParentNode} popup
   * @returns {string}
   */
  #getOverallValue(popup) {
    const overrideToggle = popup.querySelector(
      "[data-fut-card-overall-override-toggle]",
    );
    const overallInput = popup.querySelector("[data-fut-card-overall-input]");
    const overallDisplay = popup.querySelector(
      ".fut-card-stats__overall-number",
    );

    if (
      overrideToggle instanceof HTMLInputElement &&
      overrideToggle.checked &&
      overallInput instanceof HTMLInputElement
    ) {
      return overallInput.value.trim();
    }

    if (overallDisplay instanceof HTMLElement && !overallDisplay.hidden) {
      return overallDisplay.textContent?.trim() ?? "";
    }

    if (overallInput instanceof HTMLInputElement && overallInput.value.trim()) {
      return overallInput.value.trim();
    }

    return this.#calculateAverageFromStats(popup);
  }

  /**
   * @param {ParentNode} popup
   * @returns {string}
   */
  #calculateAverageFromStats(popup) {
    const values = Array.from(
      popup.querySelectorAll("[data-fut-card-stat-value]"),
    )
      .map((input) => {
        if (!(input instanceof HTMLInputElement)) return null;
        const parsed = Number.parseInt(input.value, 10);
        return Number.isNaN(parsed) ? null : parsed;
      })
      .filter((value) => value !== null);

    if (values.length === 0) return "";

    const average = Math.round(
      values.reduce((total, value) => total + value, 0) / values.length,
    );

    return String(average);
  }

  /** @param {ParentNode} popup */
  #syncStats(popup) {
    /** @type {Map<number, { label: string, value: string }>} */
    const statsByIndex = new Map();

    popup.querySelectorAll("[data-fut-card-stat-label]").forEach((element) => {
      if (!(element instanceof HTMLInputElement)) return;

      const index = Number.parseInt(element.dataset.statIndex ?? "", 10);
      if (Number.isNaN(index)) return;

      const current = statsByIndex.get(index) ?? { label: "", value: "" };
      current.label = element.value.trim().toUpperCase();
      statsByIndex.set(index, current);
    });

    popup.querySelectorAll("[data-fut-card-stat-value]").forEach((element) => {
      if (!(element instanceof HTMLInputElement)) return;

      const index = Number.parseInt(element.dataset.statIndex ?? "", 10);
      if (Number.isNaN(index)) return;

      const current = statsByIndex.get(index) ?? { label: "", value: "" };
      current.value = element.value.trim();
      statsByIndex.set(index, current);
    });

    for (const [index, slot] of this.#statSlots) {
      const stat = statsByIndex.get(index);
      const label = stat?.label ?? "";
      slot.label.textContent = label;
      slot.value.textContent = stat?.value ?? "";
      slot.label.classList.toggle(
        "fut-card-preview__stat-label--compact",
        label.length === 4,
      );
    }
  }

  /** @param {ParentNode} popup */
  #syncNationalityFlag(popup) {
    const flag = this.#flag;
    if (!flag) return;

    const flagUrl = this.#getNationalityFlagUrl(popup);

    if (!flagUrl) {
      flag.hidden = true;
      flag.removeAttribute("src");
      return;
    }

    flag.hidden = false;

    if (flag.currentSrc !== flagUrl && flag.src !== flagUrl) {
      flag.src = flagUrl;
    }
  }

  /**
   * @param {ParentNode} popup
   * @returns {string}
   */
  #getNationalityFlagUrl(popup) {
    const combobox = popup.querySelector(NATIONALITY_COMBOBOX_SELECTOR);
    if (!(combobox instanceof HTMLElement)) return "";

    const triggerIcon = combobox.querySelector("[data-combobox-trigger-icon]");
    if (triggerIcon instanceof HTMLElement && !triggerIcon.hidden) {
      const backgroundImage = triggerIcon.style.backgroundImage;
      const match = backgroundImage.match(/url\(["']?(.+?)["']?\)/);
      if (match?.[1]) {
        return match[1].split("?")[0] ?? "";
      }
    }

    const valueInput = combobox.querySelector("[data-combobox-value]");
    if (!(valueInput instanceof HTMLInputElement)) return "";

    const iso = this.#parseNationalityIso(valueInput.value);
    if (!iso) return "";

    return `${SHOPIFY_FLAG_BASE}/${iso.toLowerCase()}.svg`;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #parseNationalityIso(value) {
    const match = value.trim().match(/\[([A-Za-z]{2})\]\s*$/);
    return match?.[1]?.toUpperCase() ?? "";
  }

  /** @param {ParentNode} popup */
  #syncTeamCrest(popup) {
    const { hasFile, previewUrl } = this.#getFileUploadPreviewState(
      popup,
      PREVIEW_UPLOAD_TEAM_CREST,
    );

    this.#crestFileSelected = hasFile;

    if (!hasFile) {
      this.#crestLoadFailed = false;
      this.#applyTeamCrestDisplay("");
      return;
    }

    if (previewUrl && !this.#crestLoadFailed) {
      this.#applyTeamCrestDisplay(previewUrl);
      return;
    }

    this.#applyTeamCrestDisplay("");
  }

  /**
   * @param {string} crestUrl
   */
  #applyTeamCrestDisplay(crestUrl) {
    const crest = this.#crest;
    const crestPlaceholder = this.#crestPlaceholder;
    if (!crest) return;

    const showPlaceholder =
      this.#crestFileSelected && (!crestUrl || this.#crestLoadFailed);

    if (showPlaceholder) {
      crest.hidden = true;
      crest.removeAttribute("src");
      crestPlaceholder?.removeAttribute("hidden");
      return;
    }

    this.#setCrestTooltipOpen(false);
    crestPlaceholder?.setAttribute("hidden", "");

    if (!crestUrl) {
      crest.hidden = true;
      crest.removeAttribute("src");
      return;
    }

    crest.hidden = false;

    if (crest.currentSrc !== crestUrl && crest.src !== crestUrl) {
      crest.src = crestUrl;
    }
  }

  /** @param {ParentNode} popup */
  #syncPlayerPhoto(popup) {
    const { hasFile, previewUrl } = this.#getFileUploadPreviewState(
      popup,
      PREVIEW_UPLOAD_PLAYER_IMAGE,
    );

    if (hasFile) {
      this.#uploadFileSelected = true;
      this.#photoMode = PHOTO_MODE_UPLOAD;
      if (previewUrl !== this.#uploadUrl) {
        this.#playerPhotoLoadFailed = false;
      }
      this.#uploadUrl = previewUrl;
    } else {
      this.#uploadFileSelected = false;
      this.#uploadUrl = "";
      this.#playerPhotoLoadFailed = false;
    }

    this.#updatePhotoModeControls();
    this.#applyCurrentPlayerPhoto();
  }

  #updatePhotoModeControls() {
    this.querySelectorAll("[data-fut-card-preview-photo-mode]").forEach(
      (button) => {
        if (!(button instanceof HTMLButtonElement)) return;

        const mode = button.dataset.futCardPreviewPhotoMode;
        button.setAttribute(
          "aria-pressed",
          mode === this.#photoMode ? "true" : "false",
        );
      },
    );
  }

  #applyCurrentPlayerPhoto() {
    const playerPhoto = this.#playerPhoto;
    const playerPhotoPlaceholder = this.#playerPhotoPlaceholder;
    const examplePlayerPhoto = this.#examplePlayerPhoto;
    const examplePhotoLayer = this.#examplePhotoLayer;
    if (!playerPhoto || !examplePlayerPhoto || !examplePhotoLayer) return;

    const hasExample = this.#examplePlayerImageUrl !== "";
    const showExample = this.#photoMode === PHOTO_MODE_EXAMPLE && hasExample;
    const showUpload =
      this.#photoMode === PHOTO_MODE_UPLOAD && this.#uploadFileSelected;
    const showUploadImage =
      showUpload && this.#uploadUrl !== "" && !this.#playerPhotoLoadFailed;
    const showUploadPlaceholder =
      showUpload && (!this.#uploadUrl || this.#playerPhotoLoadFailed);

    const hidePlayerPhotoPlaceholder = () => {
      playerPhotoPlaceholder?.setAttribute("hidden", "");
    };

    const showPlayerPhotoPlaceholder = () => {
      playerPhoto.hidden = true;
      playerPhoto.removeAttribute("src");
      playerPhotoPlaceholder?.removeAttribute("hidden");
    };

    const hidePlayerPhoto = () => {
      playerPhoto.hidden = true;
      playerPhoto.removeAttribute("src");
    };

    if (!showExample && !showUpload) {
      hidePlayerPhoto();
      hidePlayerPhotoPlaceholder();
      examplePhotoLayer.hidden = true;
      examplePlayerPhoto.removeAttribute("src");
      this.#hero?.classList.remove("fut-card-preview__hero--example-photo");
      return;
    }

    if (showExample) {
      if (
        examplePlayerPhoto.currentSrc !== this.#examplePlayerImageUrl &&
        examplePlayerPhoto.src !== this.#examplePlayerImageUrl
      ) {
        examplePlayerPhoto.src = this.#examplePlayerImageUrl;
      }

      examplePhotoLayer.hidden = false;
      hidePlayerPhoto();
      hidePlayerPhotoPlaceholder();
      this.#hero?.classList.add("fut-card-preview__hero--example-photo");
      return;
    }

    examplePhotoLayer.hidden = true;
    examplePlayerPhoto.removeAttribute("src");
    this.#hero?.classList.remove("fut-card-preview__hero--example-photo");

    if (showUploadPlaceholder) {
      hidePlayerPhoto();
      showPlayerPhotoPlaceholder();
      return;
    }

    if (showUploadImage) {
      hidePlayerPhotoPlaceholder();

      if (
        playerPhoto.currentSrc !== this.#uploadUrl &&
        playerPhoto.src !== this.#uploadUrl
      ) {
        playerPhoto.src = this.#uploadUrl;
      }

      playerPhoto.hidden = false;
    }
  }

  /**
   * @param {HTMLImageElement} image
   * @returns {string}
   */
  #getDeclaredImageSrc(image) {
    const declaredSrc = image.getAttribute("src")?.trim() ?? "";
    if (!declaredSrc) return "";

    return image.currentSrc || declaredSrc;
  }

  /**
   * @param {ParentNode} popup
   * @param {string} role
   * @returns {{ hasFile: boolean, previewUrl: string }}
   */
  #getFileUploadPreviewState(popup, role) {
    const marker = popup.querySelector(
      `[data-fut-card-preview-upload="${role}"]`,
    );
    if (!(marker instanceof HTMLElement)) {
      return { hasFile: false, previewUrl: "" };
    }

    const host =
      marker.closest("fut-card-player-image-upload") ??
      marker.closest("product-file-upload") ??
      marker;

    if (!(host instanceof HTMLElement) || !host.hasAttribute("file-selected")) {
      return { hasFile: false, previewUrl: "" };
    }

    const fileInput = marker.querySelector("input[type='file']");
    if (!(fileInput instanceof HTMLInputElement) || !fileInput.files?.[0]) {
      return { hasFile: false, previewUrl: "" };
    }

    const previewImage = host.querySelector(
      ".product-file-upload__preview-image",
    );

    const previewUrl =
      previewImage instanceof HTMLImageElement
        ? this.#getDeclaredImageSrc(previewImage)
        : "";

    return { hasFile: true, previewUrl };
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindCrestPlaceholderTooltip(signal) {
    const trigger = this.#crestPlaceholder;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const tooltip = trigger.querySelector(
      "[data-fut-card-preview-crest-tooltip]",
    );
    if (!(tooltip instanceof HTMLElement)) return;

    const finePointerQuery = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    );

    trigger.addEventListener(
      "click",
      (event) => {
        if (finePointerQuery.matches) return;

        event.preventDefault();
        event.stopPropagation();
        this.#setCrestTooltipOpen(!this.#crestTooltipOpen);
      },
      { signal },
    );

    document.addEventListener(
      "click",
      (event) => {
        if (finePointerQuery.matches || !this.#crestTooltipOpen) return;

        const target = event.target;
        if (target instanceof Node && trigger.contains(target)) return;

        this.#setCrestTooltipOpen(false);
      },
      { signal },
    );

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape") {
          this.#setCrestTooltipOpen(false);
        }
      },
      { signal },
    );
  }

  /** @param {boolean} open */
  #setCrestTooltipOpen(open) {
    const trigger = this.#crestPlaceholder;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const tooltip = trigger.querySelector(
      "[data-fut-card-preview-crest-tooltip]",
    );
    if (!(tooltip instanceof HTMLElement)) return;

    this.#crestTooltipOpen = open;
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
    tooltip.toggleAttribute("data-open", open);
  }
}

if (!customElements.get("fut-card-preview")) {
  customElements.define("fut-card-preview", FutCardPreview);
}
